import { useEffect, useState } from "react";
import { MainLayout } from "../../layout/MainLayout";
import { User, emptyUser } from "../../interfaces/User";
import DataTable, { TableColumn } from "react-data-table-component";
import UserAdd from "./UserAdd";
import UserDelete from "./UserDelete";
import UserEdit from "./UserEdit";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../../elements/LoadingSpinner";
import { Form, InputGroup, Button } from "react-bootstrap";
import { handleApiError, safeJsonParse } from "../../../utils/api";

export default function Users() {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const isAdmin = localStorage.getItem('isAdmin');

    const [users, setUsers] = useState<User[]>([]);
    const [pagination, setPagination] = useState<any>({
        page: 1,
        limit: 5000,
        total: 0
    });
    const [selectedUser, setSelectedUser] = useState<User>(emptyUser);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reloadData, setReloadData] = useState(0);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const columns: TableColumn<User>[] = [
        {
            name: t("Id"),
            selector: (row: User) => row._id,
            sortable: true,
            width: '200px',
            omit: true,
        },
        {
            name: t("Mail"),
            selector: (row: User) => row.email,
            sortable: true,
            grow: 2,
        },
        {
            name: t("Login"),
            selector: (row: User) => row.username,
            sortable: true,
            width: '150px',
        },
        {
            name: t("Organization"),
            selector: (row: User) => row.org?.name || '-',
            sortable: true,
            width: '200px',
        },
        {
            name: t("Role"),
            selector: (row: User) => row.role.join(', '),
            sortable: true,
            width: '150px',
        },
        {
            name: t("Name"),
            selector: (row: User) => row.profile.firstname,
            sortable: true,
            width: '150px',
        },
        {
            name: t("Lastname"),
            selector: (row: User) => row.profile.lastname,
            sortable: true,
            width: '150px',
        },
        {
            name: t("Actions"),
            cell: (row: User) => (
                <div className="actions">
                    <a
                        className="edit-link"
                        title={t("Edit user")}
                        href="#"
                        onClick={(event) => handleShowEditForm(event, row._id)}
                    ></a>
                    <a
                        className="delete-link"
                        title={t("Delete user")}
                        href="#"
                        onClick={(event) => handleShowDeleteForm(event, row._id)}
                    ></a>
                </div>
            ),
            width: '150px',
        },
    ];

    const ExpandedComponent = ({ data }: { data: User }) => (
        <div style={{ padding: '15px' }}>
            <div><strong>{t("Id")}:</strong> {data._id}</div>
            <div><strong>{t("Mail")}:</strong> {data.email}</div>
        </div>
    );

    useEffect(() => {
        setLoading(true);
        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/user?page=' + pagination.page + '&limit=' + pagination.limit, requestOptionsPost)
            .then(response => {
                if (handleApiError(response, navigate)) {
                    return null;
                }
                return safeJsonParse(response);
            })
            .then(data => {
                if (!data) return;
                setUsers(data.users);
                setPagination(data.pagination);
                setLoading(false);
            });
    }, [reloadData]);

    const handleShowAddForm = () => {
        setShowAddForm(true);
    }

    const handleHideAddForm = () => {
        setShowAddForm(false);
    }

    const handleSumbitAddForm = (u: User) => {
        setShowAddForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowDeleteForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedUser: User = users.find((u: User) => u._id == id) ?? emptyUser;
        if (newSelectedUser._id != '') {
            setSelectedUser(newSelectedUser);
            setShowDeleteForm(true);
        } else {
            alert('No user selected.');
        }
    }
    
    const handleHideDeleteForm = () => {
        setShowDeleteForm(false);
    }

    const handleSumbitDeleteForm = (id: string) => {
        setShowDeleteForm(false);
        setReloadData(prev => prev + 1);
    }
    
    const handleShowEditForm = (event: any, id: string) => {
        event.preventDefault()
        const newSelectedUser: User = users.find((u: User) => u._id == id) ?? emptyUser;
        if (newSelectedUser._id != '') {
            setSelectedUser(newSelectedUser);
            setShowEditForm(true);
        } else {
            alert('No user selected.');
        }
    }
    
    const handleHideEditForm = () => {
        setShowEditForm(false);
    }

    const handleSumbitEditForm = (user: User) => {
        setShowEditForm(false);
        setReloadData(prev => prev + 1);
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }

    const handleClearSearch = () => {
        setSearchQuery('');
    }

    const handleToggleFilters = () => {
        setShowFilters(!showFilters);
    }

    // Filter users based on search query
    const filteredUsers = users.filter(user => {
        if (!searchQuery.trim()) return true;

        const searchLower = searchQuery.toLowerCase();
        return (
            (user.email && user.email.toLowerCase().includes(searchLower)) ||
            (user.username && user.username.toLowerCase().includes(searchLower)) ||
            (user.profile?.firstname && user.profile.firstname.toLowerCase().includes(searchLower)) ||
            (user.profile?.lastname && user.profile.lastname.toLowerCase().includes(searchLower)) ||
            (user.org?.name && user.org.name.toLowerCase().includes(searchLower)) ||
            (user.role && user.role.join(', ').toLowerCase().includes(searchLower))
        );
    });

    return (
        <MainLayout>
            <div className="container-fluid">
                <div className="row">
                    { isAdmin &&
                        <div className="admin-users-container">
                                    <div className="page-header">
                                        <h1>{ t("Users management") }</h1>
                                        <div className="page-actions">
                                            <button
                                                className={`filter-toggle ${showFilters ? 'active' : 'inactive'}`}
                                                onClick={handleToggleFilters}
                                                title={t("Filters")}
                                            >
                                                <i className="bi bi-funnel"></i>
                                            </button>
                                            <a className="action-icon" onClick={ handleShowAddForm } title={ t("New user") }>
                                                <i className="bi bi-plus-circle"></i>
                                            </a>
                                        </div>
                                    </div>

                                    {/* Search controls */}
                                    <div className={`user-filters ${!showFilters ? 'hidden' : ''}`}>
                                        <div className="filter-controls">
                                            <Form.Group className="search-group">
                                                <Form.Label>{ t("Search") }</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder={ t("Search by email, username, name, organization...") }
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    {searchQuery && (
                                                        <Button variant="secondary" onClick={handleClearSearch}>
                                                            { t("Clear") }
                                                        </Button>
                                                    )}
                                                </InputGroup>
                                            </Form.Group>
                                        </div>

                                        <div className="filter-info">
                                            { t("Total results") }: <strong>{filteredUsers.length}</strong> / {users.length}
                                            {searchQuery && (
                                                <span className="search-term">
                                                    { t("Searching for") }: <strong>"{searchQuery}"</strong>
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="admin-users-table-container">
                                        <DataTable
                                            columns={columns}
                                            data={filteredUsers}
                                            progressPending={loading}
                                            progressComponent={<LoadingSpinner />}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                            paginationComponentOptions={{
                                                rowsPerPageText: t('Rows per page') + ':',
                                                rangeSeparatorText: t('of'),
                                            }}
                                            highlightOnHover
                                            striped
                                            responsive
                                            noDataComponent={t("No users found")}
                                            expandableRows
                                            expandableRowsComponent={ExpandedComponent}
                                        />
                                    </div>
                                </div>
                            }
                    { !isAdmin &&
                        <div className="admin-users-container">
                            { t("You are not allowed to access this section.") }
                        </div>
                    }
                </div>
            </div>
            { showAddForm &&
                <UserAdd submitCallback={ handleSumbitAddForm } closeCallback={ handleHideAddForm } />
            }
            { showDeleteForm &&
                <UserDelete user={ selectedUser } submitCallback={ handleSumbitDeleteForm } closeCallback={ handleHideDeleteForm } />
            }
            { showEditForm &&
                <UserEdit user={ selectedUser } submitCallback={ handleSumbitEditForm } closeCallback={ handleHideEditForm } />
            }
        </MainLayout>);
}